import { useEffect } from "react";
import { useLocation } from "react-router";
import { useChatStore } from "@/stores/useChatStore";
import { useLayoutStore } from "@/stores/useLayoutStore";

/**
 * セグメンテーション HITL の有無に合わせて、下部ペインのセグメンテーションタブを開閉する。
 * リクエストが届いたら開始 (start)、チャットから離れたら中断 (suspend、記録は残す)、
 * 同じチャットで応答済みになったら解決 (restore、記録を捨てる)、読み込みが完了して
 * 未解決のリクエストが無ければ記録だけ破棄する (purge)。表示はいずれも
 * 「最後に手動で選んだ下部ペインの状態」へ戻る。
 */
export function useSegmentationPaneSync(): void {
	const { pathname } = useLocation();
	const chatId = useChatStore((s) => s.chatId);
	const toolUseId = useChatStore((s) =>
		s.hitlState?.kind === "segmentation" ? s.hitlState.toolUseId : null,
	);
	const isLoadingChat = useChatStore((s) => s.isLoadingChat);
	const chatLoadFailed = useChatStore((s) => s.chatLoadFailed);
	const startSegmentationPane = useLayoutStore((s) => s.startSegmentationPane);
	const suspendSegmentationPane = useLayoutStore((s) => s.suspendSegmentationPane);
	const restoreSegmentationPane = useLayoutStore((s) => s.restoreSegmentationPane);
	const discardSegmentationPaneShowState = useLayoutStore(
		(s) => s.discardSegmentationPaneShowState,
	);

	useEffect(() => {
		const isChatlessRoute =
			pathname === "/chats/new" || (pathname !== "/" && !pathname.startsWith("/chats/"));
		const pane = useLayoutStore.getState().segmentationPane;
		if (isChatlessRoute) {
			if (pane) suspendSegmentationPane();
			return;
		}
		if (chatId && toolUseId) {
			startSegmentationPane(chatId, toolUseId);
			return;
		}
		if (pane) {
			// 同じチャットで読込中でなければ「解決」。それ以外は離脱・再読込なので記録を残す。
			if (pane.chatId === chatId && !isLoadingChat) restoreSegmentationPane();
			else suspendSegmentationPane();
			return;
		}
		// 読み込みに失敗した場合は「未解決かどうか」を判定できないため、記録を残す。
		if (chatId && !isLoadingChat && !chatLoadFailed) discardSegmentationPaneShowState(chatId);
	}, [
		pathname,
		chatId,
		toolUseId,
		isLoadingChat,
		chatLoadFailed,
		startSegmentationPane,
		suspendSegmentationPane,
		restoreSegmentationPane,
		discardSegmentationPaneShowState,
	]);
}
